import React from 'react'
import type { TextChain } from '@/api/types/stowed-element'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'
import { collectLinkableFrames } from '@/lib/text-linking/linkable-text-frame'
import { resolveChainsToFrames } from '@/lib/text-linking/text-linking-geometry'
import { framesShowingOverflowBadge } from '@/components/app/shapes/shape-text-frame-registry'

import { computeLinkingSignature } from '../helpers/linking-signature'

interface PolledFrames {
  frames: LinkableTextFrame[]
  chains: LinkableTextFrame[][]
  /** Frames painting `TextOverflowBadge` right now — see `TextLinkingLayerProps`. */
  overflowBadgeFrameIds: ReadonlySet<string>
}

const EMPTY_FRAMES: PolledFrames = {
  frames: [],
  chains: [],
  overflowBadgeFrameIds: new Set<string>(),
}

/**
 * Live frame geometry and chain topology, sampled on an animation frame.
 *
 * The same pattern (and the same geometry fingerprint) as `usePolledCanvas` in
 * `visual-layer/hooks/use-polled-canvas.ts`, and for the same reason — deliberately so, because these two layers draw against each other and
 * any difference in update cadence shows up as chrome that separates.
 *
 * Ports used to be recomputed inline on every `center-canvas` render and nudged by
 * `setTextLinkingRevision`, which is bumped from exactly two places, both on the *outbound* commit
 * path. Undo/redo and remote multiplayer edits write straight into `shapeManagerRef.current
 * .updateShape(...)` and reach neither, so the frame moved, the handles followed it (they poll) and
 * the ports stayed behind — misalignment that accumulated "while working" rather than appearing at
 * once (WMPCM-9199). Polling both layers on the same signal removes the asymmetry, and also the
 * one-frame lag the old `setState`-inside-rAF path had during drags.
 *
 * The fingerprint (`computeLinkingSignature`) is deliberately wider than the visual layer's: a
 * frame joining or leaving a chain moves ports without moving any geometry, so chain topology, uid
 * and lock state all join the key. The overflow-badge flag joins it for the same reason — it
 * changes whether the out-port "+" draws its container without moving anything (WMPCM-9868).
 */
export function usePolledLinkingFrames(
  shapeManagerRef: React.RefObject<ShapeManagerRef | null>,
  linkedTextChains: TextChain[]
): PolledFrames {
  const [polled, setPolled] = React.useState<PolledFrames>(EMPTY_FRAMES)
  const signatureRef = React.useRef('')
  const chainsRef = React.useRef(linkedTextChains)

  chainsRef.current = linkedTextChains

  React.useEffect(() => {
    let frame = 0
    const tick = () => {
      const shapes = shapeManagerRef.current?.getShapes?.() ?? []
      const chains = chainsRef.current
      const frames = collectLinkableFrames(shapes)
      const overflowBadgeFrameIds = framesShowingOverflowBadge(frames.map((f) => f.id))
      const signature = computeLinkingSignature({ shapes, frames, overflowBadgeFrameIds, chains })

      if (signature !== signatureRef.current) {
        signatureRef.current = signature
        setPolled({
          frames,
          chains: resolveChainsToFrames(chains, frames),
          overflowBadgeFrameIds,
        })
      }

      frame = window.requestAnimationFrame(tick)
    }

    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [shapeManagerRef])

  return polled
}
